import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, getDocs, addDoc, doc, getDoc, updateDoc, serverTimestamp, arrayRemove, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../firebase/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import { FaGem, FaCoins, FaTrophy, FaPlay, FaHistory, FaInfoCircle, FaSpinner, FaShippingFast } from 'react-icons/fa';
import ClawWinningsShipping from '../components/claw/ClawWinningsShipping';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const TreasureHuntPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const videoRef = useRef(null);
  
  const [gameState, setGameState] = useState('idle'); // idle, paying, playing, revealing, won
  const [prizeTiers, setPrizeTiers] = useState([]);
  const [gameConfig, setGameConfig] = useState(null);
  const [wonPrize, setWonPrize] = useState(null);
  const [playHistory, setPlayHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [pendingWinnings, setPendingWinnings] = useState([]);
  const [showShippingModal, setShowShippingModal] = useState(false);
  const [shippingSuccess, setShippingSuccess] = useState(false);

  // Handle shipping payment success callback
  useEffect(() => {
    const handleShippingSuccess = async () => {
      const params = new URLSearchParams(location.search);
      const shippingStatus = params.get('shipping');
      const requestId = params.get('request_id');
      
      if (shippingStatus === 'success' && requestId && currentUser) {
        try {
          // Update shipping request status to paid
          await updateDoc(doc(db, 'clawShippingRequests', requestId), {
            status: 'paid',
            paidAt: serverTimestamp()
          });
          
          // Get the shipping request to find which items to update
          const requestDoc = await getDoc(doc(db, 'clawShippingRequests', requestId));
          if (requestDoc.exists()) {
            const requestData = requestDoc.data();
            
            // Mark each item as shipping requested
            for (const item of requestData.items || []) {
              if (item.playId) {
                await updateDoc(doc(db, 'users', currentUser.uid, 'treasureHuntPlays', item.playId), {
                  shippingRequested: true,
                  shippingRequestId: requestId,
                  shippingRequestedAt: serverTimestamp(),
                  status: 'shipping_requested'
                });
              }
            }
          }
          
          setShippingSuccess(true);
          
          // Clear URL params
          navigate('/treasure-hunt', { replace: true });
          
          // Refresh pending winnings
          const historySnapshot = await getDocs(
            collection(db, 'users', currentUser.uid, 'treasureHuntPlays')
          );
          const history = historySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          const pending = history.filter(h => 
            (h.status === 'pending_shipment' || h.status === 'won') && !h.shippingRequested
          );
          setPendingWinnings(pending);
          setPlayHistory(history.sort((a, b) => b.playedAt?.toDate() - a.playedAt?.toDate()));
          
        } catch (err) {
          console.error('Error updating shipping request:', err);
        }
      }
    };
    
    handleShippingSuccess();
  }, [location.search, currentUser, navigate]);

  // Fetch game configuration and prize tiers
  useEffect(() => {
    const fetchGameData = async () => {
      try {
        // Fetch game config
        const configDoc = await getDoc(doc(db, 'settings', 'treasureHunt'));
        if (configDoc.exists()) {
          setGameConfig(configDoc.data());
        } else {
          // Default config if not set
          setGameConfig({
            pricePerPlay: 20,
            enabled: true,
            videoUrl: '/videos/claw-animation.mp4',
            title: 'Treasure Hunt',
            description: 'Try your luck with our virtual claw machine!'
          });
        }

        // Fetch prize tiers
        const tiersSnapshot = await getDocs(collection(db, 'treasureHuntPrizes'));
        const tiers = tiersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })).sort((a, b) => b.odds - a.odds); // Sort by odds (highest first)
        setPrizeTiers(tiers);

        // Fetch user's play history if logged in
        if (currentUser) {
          const historySnapshot = await getDocs(
            collection(db, 'users', currentUser.uid, 'treasureHuntPlays')
          );
          const history = historySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })).sort((a, b) => b.playedAt?.toDate() - a.playedAt?.toDate());
          setPlayHistory(history);
          
          // Filter pending winnings that haven't been shipped
          const pending = history.filter(h => 
            h.status === 'pending_shipment' || h.status === 'won'
          );
          setPendingWinnings(pending);
        }
      } catch (err) {
        console.error('Error fetching game data:', err);
        setError('Failed to load game data');
      } finally {
        setLoading(false);
      }
    };

    fetchGameData();
  }, [currentUser]);

  // Weighted random selection algorithm - selects and removes item from pool
  const selectPrize = (tiers) => {
    // Filter tiers that have items available
    const availableTiers = tiers.filter(tier => tier.items && tier.items.length > 0);
    
    if (availableTiers.length === 0) {
      // No items available, return generic tier prize
      return { tier: tiers[0], item: null, itemIndex: -1 };
    }
    
    const totalOdds = availableTiers.reduce((sum, tier) => sum + tier.odds, 0);
    let random = Math.random() * totalOdds;
    
    for (const tier of availableTiers) {
      random -= tier.odds;
      if (random <= 0) {
        // Select a random item from this tier
        const items = tier.items || [];
        if (items.length > 0) {
          const randomIndex = Math.floor(Math.random() * items.length);
          const randomItem = items[randomIndex];
          return { tier, item: randomItem, itemIndex: randomIndex };
        }
        return { tier, item: null, itemIndex: -1 };
      }
    }
    
    // Fallback to first available tier
    const fallbackTier = availableTiers[0];
    const fallbackItem = fallbackTier?.items?.[0] || null;
    return { tier: fallbackTier || tiers[0], item: fallbackItem, itemIndex: fallbackItem ? 0 : -1 };
  };
  
  // Remove won item from the prize pool
  const removeWonItemFromPool = async (tierId, item) => {
    if (!item || !item.id) return;
    
    try {
      const tierRef = doc(db, 'treasureHuntPrizes', tierId);
      await updateDoc(tierRef, {
        items: arrayRemove(item)
      });
      console.log('Item removed from prize pool:', item.name);
    } catch (error) {
      console.error('Error removing item from pool:', error);
    }
  };

  // Handle play button click
  const handlePlay = async () => {
    if (!currentUser) {
      navigate('/login', { state: { from: '/treasure-hunt' } });
      return;
    }

    if (!gameConfig?.enabled) {
      setError('Treasure Hunt is currently unavailable');
      return;
    }

    setGameState('paying');
    setError('');

    try {
      const stripe = await stripePromise;
      
      // Create checkout session for the play
      const response = await fetch('https://benevolent-sfogliatella-83141e.netlify.app/.netlify/functions/create-treasure-hunt-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerEmail: currentUser.email,
          pricePerPlay: gameConfig.pricePerPlay,
          userId: currentUser.uid,
          successUrl: `${window.location.origin}/treasure-hunt?play=true&session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/treasure-hunt?canceled=true`
        })
      });

      const session = await response.json();
      
      if (session.error) {
        throw new Error(session.error);
      }

      // Redirect to Stripe Checkout
      const result = await stripe.redirectToCheckout({ sessionId: session.id });
      
      if (result.error) {
        throw new Error(result.error.message);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError('Payment failed. Please try again.');
      setGameState('idle');
    }
  };

  // Check for successful payment return
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shouldPlay = urlParams.get('play');
    const sessionId = urlParams.get('session_id');
    
    if (shouldPlay === 'true' && sessionId && gameState === 'idle' && prizeTiers.length > 0) {
      // Clear URL params
      window.history.replaceState({}, '', '/treasure-hunt');
      
      // Start the game
      playGame(sessionId);
    }
  }, [prizeTiers, gameState]);

  const playGame = async (sessionId) => {
    setGameState('playing');
    
    // Play the claw animation video with proper handling
    if (videoRef.current) {
      const video = videoRef.current;
      console.log('Starting video playback...');
      console.log('Video src:', video.src);
      console.log('Video readyState:', video.readyState);
      
      // Reset video to start
      video.currentTime = 0;
      video.muted = true; // Must be muted for browser autoplay
      video.volume = 1;
      
      // Function to attempt playback
      const attemptPlay = async () => {
        try {
          const playPromise = video.play();
          if (playPromise !== undefined) {
            await playPromise;
            console.log('Video playing successfully');
          }
        } catch (error) {
          console.error('Video play error:', error);
        }
      };
      
      // If video is ready, play immediately
      if (video.readyState >= 3) {
        await attemptPlay();
      } else {
        // Wait for video to be ready
        video.load();
        video.addEventListener('canplaythrough', async function onCanPlay() {
          video.removeEventListener('canplaythrough', onCanPlay);
          await attemptPlay();
        }, { once: true });
        
        // Also try playing on loadeddata as fallback
        video.addEventListener('loadeddata', async function onLoaded() {
          video.removeEventListener('loadeddata', onLoaded);
          if (video.paused) {
            await attemptPlay();
          }
        }, { once: true });
      }
    } else {
      console.log('Video ref not available');
    }

    // Determine the prize using weighted random selection
    const result = selectPrize(prizeTiers);
    
    // Wait for video to finish (or timeout after video duration)
    const videoDuration = gameConfig?.videoDuration || 5000;
    
    setTimeout(async () => {
      setWonPrize(result);
      setGameState('revealing');
      
      // Save play result to Firestore
      try {
        const playData = {
          sessionId,
          tierName: result.tier.name,
          tierId: result.tier.id,
          tierValue: result.tier.value,
          itemId: result.item?.id || null,
          itemName: result.item?.name || result.tier.name,
          itemImage: result.item?.image || result.tier.image,
          itemValue: result.item?.value || result.tier.value,
          playedAt: serverTimestamp(),
          status: 'pending_shipment',
          shippingRequested: false,
          pricePaid: gameConfig.pricePerPlay
        };
        
        await addDoc(collection(db, 'users', currentUser.uid, 'treasureHuntPlays'), playData);

        // Also add to global treasure hunt plays for admin
        await addDoc(collection(db, 'treasureHuntPlays'), {
          ...playData,
          userId: currentUser.uid,
          userEmail: currentUser.email
        });
        
        // IMPORTANT: Remove the won item from the prize pool
        if (result.item && result.tier.id) {
          await removeWonItemFromPool(result.tier.id, result.item);
        }

        // Refresh play history
        const historySnapshot = await getDocs(
          collection(db, 'users', currentUser.uid, 'treasureHuntPlays')
        );
        const history = historySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })).sort((a, b) => b.playedAt?.toDate() - a.playedAt?.toDate());
        setPlayHistory(history);
        
        // Update pending winnings
        const pending = history.filter(h => 
          h.status === 'pending_shipment' || h.status === 'won'
        );
        setPendingWinnings(pending);
      } catch (err) {
        console.error('Error saving play result:', err);
      }

      // Transition to won state after reveal animation
      setTimeout(() => {
        setGameState('won');
      }, 2000);
    }, videoDuration);
  };

  const resetGame = () => {
    setGameState('idle');
    setWonPrize(null);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <FaSpinner className="spin" />
          <p>Loading Treasure Hunt...</p>
        </LoadingContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          <GameTitle>
            <FaTrophy /> {gameConfig?.title || 'Treasure Hunt'}
          </GameTitle>
          <GameSubtitle>
            {gameConfig?.description || 'Try your luck with our virtual claw machine!'}
          </GameSubtitle>
          <PriceTag>${gameConfig?.pricePerPlay || 20} per play</PriceTag>
        </HeroContent>
      </HeroSection>

      <MainContent>
        <GameSection>
          <GameMachine>
            {/* Video Animation Layer */}
            <VideoContainer $visible={gameState === 'playing'}>
              <video
                ref={videoRef}
                src={gameConfig?.videoUrl || '/videos/claw-animation.mp4'}
                muted
                playsInline
                autoPlay={gameState === 'playing'}
                preload="auto"
                loop={false}
                onCanPlay={() => console.log('Video can play, readyState:', videoRef.current?.readyState)}
                onPlay={() => console.log('Video started playing')}
                onPlaying={() => console.log('Video is playing')}
                onError={(e) => console.error('Video error:', e)}
              >
                <track kind="captions" />
              </video>
            </VideoContainer>

            {/* Idle State */}
            {gameState === 'idle' && (
              <IdleDisplay>
                {gameConfig?.idleImage ? (
                  <IdleImage src={gameConfig.idleImage} alt="Ready to play" />
                ) : (
                  <ClawIcon>🪙</ClawIcon>
                )}
                <IdleText>{gameConfig?.idleTitle || 'Ready to play?'}</IdleText>
                <IdleSubtext>{gameConfig?.idleSubtext || 'Press the button below to try your luck!'}</IdleSubtext>
              </IdleDisplay>
            )}

            {/* Paying State */}
            {gameState === 'paying' && (
              <PayingDisplay>
                <FaSpinner className="spin" size={50} />
                <p>Redirecting to payment...</p>
              </PayingDisplay>
            )}

            {/* Playing State - shows if video fails */}
            {gameState === 'playing' && (
              <PlayingDisplay>
                <FaSpinner className="spin" size={60} />
                <p>The claw is grabbing your prize...</p>
              </PlayingDisplay>
            )}

            {/* Revealing State */}
            {gameState === 'revealing' && wonPrize && (
              <RevealDisplay>
                <RevealText>You won!</RevealText>
                <PrizeReveal>
                  {wonPrize.item?.image ? (
                    <PrizeImage src={wonPrize.item.image} alt={wonPrize.item.name} />
                  ) : wonPrize.tier?.image ? (
                    <PrizeImage src={wonPrize.tier.image} alt={wonPrize.tier.name} />
                  ) : (
                    <PrizePlaceholder>
                      <FaGem size={80} />
                    </PrizePlaceholder>
                  )}
                </PrizeReveal>
              </RevealDisplay>
            )}

            {/* Won State */}
            {gameState === 'won' && wonPrize && (
              <WonDisplay>
                <Confetti />
                <WonTitle>🎉 Congratulations! 🎉</WonTitle>
                <WonPrize>
                  {wonPrize.item?.image ? (
                    <WonPrizeImage src={wonPrize.item.image} alt={wonPrize.item.name} />
                  ) : wonPrize.tier?.image ? (
                    <WonPrizeImage src={wonPrize.tier.image} alt={wonPrize.tier.name} />
                  ) : (
                    <WonPrizePlaceholder>
                      <FaGem size={60} />
                    </WonPrizePlaceholder>
                  )}
                  <WonPrizeName>{wonPrize.item?.name || wonPrize.tier?.name}</WonPrizeName>
                  <WonPrizeValue>Value: ${wonPrize.item?.value || wonPrize.tier?.value}</WonPrizeValue>
                </WonPrize>
                <WonMessage>
                  Your prize has been added to your winnings!
                </WonMessage>
                <WonButtonGroup>
                  <RequestShippingButton onClick={() => setShowShippingModal(true)}>
                    📦 Request Shipping
                  </RequestShippingButton>
                  <PlayAgainButton onClick={resetGame}>
                    <FaPlay /> Play Again
                  </PlayAgainButton>
                </WonButtonGroup>
              </WonDisplay>
            )}
          </GameMachine>

          {/* Play Button */}
          {(gameState === 'idle') && (
            <PlayButton onClick={handlePlay} disabled={!gameConfig?.enabled}>
              <FaPlay /> Play Now - ${gameConfig?.pricePerPlay || 20}
            </PlayButton>
          )}

          {error && <ErrorMessage>{error}</ErrorMessage>}
        </GameSection>

        <InfoSection>
          {/* Prize Tiers */}
          <InfoCard>
            <InfoCardHeader onClick={() => setShowRules(!showRules)}>
              <h3><FaInfoCircle /> How It Works</h3>
            </InfoCardHeader>
            <InfoCardContent $expanded={showRules}>
              <RulesList>
                <li>Pay ${gameConfig?.pricePerPlay || 20} for one play</li>
                <li>Watch the claw grab your prize</li>
                <li>Your prize is revealed and shipped to you!</li>
                <li>Prizes range from copper rounds to full silver ounces</li>
              </RulesList>
            </InfoCardContent>
          </InfoCard>

          <InfoCard>
            <InfoCardHeader>
              <h3><FaCoins /> Prize Tiers</h3>
            </InfoCardHeader>
            <InfoCardContent $expanded={true}>
              <TiersList>
                {prizeTiers.map(tier => (
                  <TierItem key={tier.id}>
                    <TierIcon>
                      {tier.icon === 'gold' ? '🥇' : 
                       tier.icon === 'silver' ? '🥈' : 
                       tier.icon === 'bronze' ? '🥉' : '🎁'}
                    </TierIcon>
                    <TierInfo>
                      <TierName>{tier.name}</TierName>
                      <TierOdds>{tier.odds}% chance</TierOdds>
                    </TierInfo>
                    <TierValue>${tier.value}+</TierValue>
                  </TierItem>
                ))}
              </TiersList>
            </InfoCardContent>
          </InfoCard>

          {/* Play History */}
          {currentUser && (
            <InfoCard>
              <InfoCardHeader onClick={() => setShowHistory(!showHistory)}>
                <h3><FaHistory /> Your History</h3>
                <HistoryCount>{playHistory.length} plays</HistoryCount>
              </InfoCardHeader>
              <InfoCardContent $expanded={showHistory}>
                {playHistory.length > 0 ? (
                  <HistoryList>
                    {playHistory.slice(0, 10).map(play => (
                      <HistoryItem key={play.id}>
                        <HistoryDate>
                          {play.playedAt?.toDate()?.toLocaleDateString() || 'Recent'}
                        </HistoryDate>
                        <HistoryPrize>{play.itemName}</HistoryPrize>
                        <HistoryStatus $status={play.status}>
                          {play.status === 'shipped' ? 'Shipped' : 
                           play.status === 'delivered' ? 'Delivered' : 'Pending'}
                        </HistoryStatus>
                      </HistoryItem>
                    ))}
                  </HistoryList>
                ) : (
                  <EmptyHistory>No plays yet. Try your luck!</EmptyHistory>
                )}
              </InfoCardContent>
            </InfoCard>
          )}
        </InfoSection>
      </MainContent>
      
      {/* Pending Winnings Banner */}
      {currentUser && pendingWinnings.length > 0 && gameState === 'idle' && (
        <PendingWinningsBanner>
          <BannerContent>
            <BannerIcon><FaShippingFast /></BannerIcon>
            <BannerText>
              <strong>You have {pendingWinnings.length} prize{pendingWinnings.length > 1 ? 's' : ''} waiting to be shipped!</strong>
              <span>Request shipping to receive your winnings.</span>
            </BannerText>
            <BannerButton onClick={() => setShowShippingModal(true)}>
              Request Shipping
            </BannerButton>
          </BannerContent>
        </PendingWinningsBanner>
      )}
      
      {/* Shipping Modal */}
      <ClawWinningsShipping
        isOpen={showShippingModal}
        onClose={() => setShowShippingModal(false)}
        onSuccess={() => {
          setShowShippingModal(false);
          // Refresh pending winnings
          if (currentUser) {
            getDocs(collection(db, 'users', currentUser.uid, 'treasureHuntPlays'))
              .then(snapshot => {
                const history = snapshot.docs.map(doc => ({
                  id: doc.id,
                  ...doc.data()
                }));
                const pending = history.filter(h => 
                  (h.status === 'pending_shipment' || h.status === 'won') && !h.shippingRequested
                );
                setPendingWinnings(pending);
              });
          }
        }}
      />
    </PageContainer>
  );
};

// Animations
const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(212, 175, 55, 0.5); }
  50% { box-shadow: 0 0 40px rgba(212, 175, 55, 0.8); }
`;

const confettiDrop = keyframes`
  0% { transform: translateY(-100%) rotate(0deg); opacity: 1; }
  100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.8); }
  to { opacity: 1; transform: scale(1); }
`;

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  color: #D4AF37;
  
  .spin {
    animation: ${spin} 1s linear infinite;
    font-size: 3rem;
    margin-bottom: 1rem;
  }
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, #D4AF37 0%, #F4D03F 50%, #D4AF37 100%);
  padding: 2rem 1.5rem;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const GameTitle = styled.h1`
  font-size: 2.5rem;
  color: #1a1a2e;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
    gap: 0.5rem;
    margin-bottom: 0.15rem;
  }
`;

const GameSubtitle = styled.p`
  font-size: 1.1rem;
  color: #2a2a4a;
  margin-bottom: 0.75rem;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
    margin-bottom: 0.35rem;
    display: none;
  }
`;

const PriceTag = styled.div`
  display: inline-block;
  background: #1a1a2e;
  color: #D4AF37;
  padding: 0.75rem 2rem;
  border-radius: 50px;
  font-size: 1.5rem;
  font-weight: bold;
  
  @media (max-width: 768px) {
    padding: 0.4rem 1.25rem;
    font-size: 1.1rem;
  }
`;

const MainContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    padding: 0.5rem;
  }
`;

const GameSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const GameMachine = styled.div`
  width: 100%;
  max-width: 1000px;
  aspect-ratio: 4/3;
  background: linear-gradient(145deg, #2a2a4a, #1a1a2e);
  border-radius: 20px;
  border: 4px solid #D4AF37;
  overflow: hidden;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  margin: 0 auto;
  
  @media (max-width: 768px) {
    max-width: calc(100vw - 1rem);
    min-height: 450px;
    aspect-ratio: auto;
    border-radius: 8px;
    border-width: 2px;
  }
`;

const VideoContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: ${props => props.$visible ? 10 : 0};
  opacity: ${props => props.$visible ? 1 : 0};
  pointer-events: ${props => props.$visible ? 'auto' : 'none'};
  transition: opacity 0.3s, z-index 0s;
  background: #1a1a2e;
  display: flex;
  align-items: center;
  justify-content: center;
  
  video {
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #000;
  }
`;

const IdleDisplay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ClawIcon = styled.div`
  font-size: 5rem;
  margin-bottom: 1rem;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const IdleImage = styled.img`
  max-width: 400px;
  max-height: 400px;
  margin-bottom: 1rem;
  animation: ${pulse} 2s ease-in-out infinite;
  object-fit: contain;
`;

const IdleText = styled.h2`
  color: #D4AF37;
  font-size: 2rem;
  margin-bottom: 0.5rem;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const IdleSubtext = styled.p`
  color: #aaa;
  font-size: 1.1rem;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const PayingDisplay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #D4AF37;
  
  .spin {
    animation: ${spin} 1s linear infinite;
    margin-bottom: 1rem;
  }
`;

const PlayingDisplay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #D4AF37;
  background: rgba(26, 26, 46, 0.9);
  z-index: 5;
  
  p {
    margin-top: 1rem;
    font-size: 1.3rem;
    text-align: center;
  }
  
  .spin {
    animation: ${spin} 1s linear infinite;
  }
`;

const RevealDisplay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  animation: ${fadeIn} 0.5s ease-out;
`;

const RevealText = styled.h2`
  color: #D4AF37;
  font-size: 2.5rem;
  margin-bottom: 1rem;
  text-shadow: 0 0 20px rgba(212, 175, 55, 0.8);
  animation: ${glow} 2s ease-in-out infinite;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const WonText = styled.h2`
  color: #D4AF37;
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  text-shadow: 0 0 20px rgba(212, 175, 55, 0.8);
  animation: ${glow} 2s ease-in-out infinite;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const PrizeReveal = styled.div`
  animation: ${glow} 1s ease-in-out infinite;
  border-radius: 50%;
  padding: 1rem;
`;

const PrizeImage = styled.img`
  width: 150px;
  height: 150px;
  object-fit: contain;
  border-radius: 10px;
`;

const PrizePlaceholder = styled.div`
  width: 150px;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #D4AF37;
`;

const WonDisplay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
  padding: 1rem;
  position: relative;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem;
  }
`;

const Confetti = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  
  &::before, &::after {
    content: '🎉🎊✨⭐🌟';
    position: absolute;
    font-size: 2rem;
    animation: ${confettiDrop} 3s linear infinite;
  }
  
  &::before {
    left: 20%;
    animation-delay: 0s;
  }
  
  &::after {
    left: 70%;
    animation-delay: 1.5s;
  }
`;

const WonTitle = styled.h2`
  color: #D4AF37;
  font-size: 2rem;
  margin-bottom: 1rem;
  animation: ${pulse} 0.5s ease-out;
`;

const WonPrize = styled.div`
  text-align: center;
  margin-bottom: 1rem;
`;

const WonPrizeImage = styled.img`
  width: 180px;
  height: 180px;
  object-fit: contain;
  border-radius: 12px;
  margin-bottom: 0.75rem;
  border: 3px solid #D4AF37;
  box-shadow: 0 8px 32px rgba(212, 175, 55, 0.3);
`;

const WonPrizePlaceholder = styled.div`
  width: 180px;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #D4AF37;
  margin: 0 auto 0.75rem;
`;

const WonPrizeName = styled.h3`
  color: white;
  font-size: 1.3rem;
  margin-bottom: 0.25rem;
`;

const WonPrizeValue = styled.p`
  color: #4CAF50;
  font-size: 1.1rem;
  font-weight: bold;
`;

const WonMessage = styled.p`
  color: #aaa;
  font-size: 0.9rem;
  text-align: center;
  margin-bottom: 1rem;
`;

const PlayButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 2rem;
  padding: 1.25rem 3rem;
  font-size: 1.5rem;
  font-weight: bold;
  color: #1a1a2e;
  background: linear-gradient(135deg, #D4AF37 0%, #F4D03F 50%, #D4AF37 100%);
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s;
  animation: ${glow} 2s ease-in-out infinite;
  
  &:hover {
    transform: scale(1.05);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    animation: none;
  }
  
  @media (max-width: 768px) {
    margin-top: 1rem;
    padding: 1rem 2rem;
    font-size: 1.25rem;
  }
`;

const PlayAgainButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 2rem;
  font-size: 1.1rem;
  font-weight: bold;
  color: #1a1a2e;
  background: linear-gradient(135deg, #D4AF37 0%, #F4D03F 100%);
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const ErrorMessage = styled.p`
  color: #ff6b6b;
  margin-top: 1rem;
  padding: 0.75rem 1.5rem;
  background: rgba(255, 107, 107, 0.1);
  border-radius: 8px;
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const InfoCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  overflow: hidden;
  border: 1px solid rgba(212, 175, 55, 0.3);
  
  @media (max-width: 768px) {
    border-radius: 8px;
  }
`;

const InfoCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem;
  background: rgba(212, 175, 55, 0.1);
  cursor: pointer;
  
  h3 {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    color: #D4AF37;
    font-size: 1.1rem;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    
    h3 {
      font-size: 0.95rem;
    }
  }
`;

const InfoCardContent = styled.div`
  padding: ${props => props.$expanded ? '1.5rem' : '0 1.5rem'};
  max-height: ${props => props.$expanded ? '500px' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
  color: white;
  
  @media (max-width: 768px) {
    padding: ${props => props.$expanded ? '1rem' : '0 1rem'};
  }
`;

const RulesList = styled.ol`
  color: #ddd;
  padding-left: 1.25rem;
  margin: 0;
  
  li {
    margin-bottom: 0.5rem;
    line-height: 1.5;
  }
`;

const TiersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const TierItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
`;

const TierIcon = styled.div`
  font-size: 1.5rem;
`;

const TierInfo = styled.div`
  flex: 1;
`;

const TierName = styled.div`
  color: white;
  font-weight: 500;
`;

const TierOdds = styled.div`
  color: #aaa;
  font-size: 0.85rem;
`;

const TierValue = styled.div`
  color: #4CAF50;
  font-weight: bold;
`;

const HistoryCount = styled.span`
  color: #aaa;
  font-size: 0.9rem;
`;

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const HistoryItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
`;

const HistoryDate = styled.div`
  color: #aaa;
  font-size: 0.85rem;
`;

const HistoryPrize = styled.div`
  color: white;
  flex: 1;
  margin-left: 1rem;
`;

const HistoryStatus = styled.div`
  font-size: 0.8rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  background: ${props => 
    props.$status === 'delivered' ? 'rgba(76, 175, 80, 0.2)' :
    props.$status === 'shipped' ? 'rgba(33, 150, 243, 0.2)' :
    'rgba(255, 193, 7, 0.2)'
  };
  color: ${props => 
    props.$status === 'delivered' ? '#4CAF50' :
    props.$status === 'shipped' ? '#2196F3' :
    '#FFC107'
  };
`;

const EmptyHistory = styled.p`
  color: #aaa;
  text-align: center;
  padding: 1rem;
`;

const WonButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: center;
`;

const RequestShippingButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: bold;
  color: white;
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 15px rgba(76, 175, 80, 0.4);
  }
`;

const PendingWinningsBanner = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, #D4AF37 0%, #F4D03F 100%);
  padding: 1rem 2rem;
  z-index: 100;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
  }
`;

const BannerContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
    text-align: center;
  }
`;

const BannerIcon = styled.div`
  font-size: 2rem;
  color: #1a1a2e;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const BannerText = styled.div`
  flex: 1;
  color: #1a1a2e;
  
  strong {
    display: block;
    font-size: 1.1rem;
    margin-bottom: 0.25rem;
  }
  
  span {
    font-size: 0.9rem;
    opacity: 0.8;
  }
  
  @media (max-width: 768px) {
    strong {
      font-size: 0.9rem;
      margin-bottom: 0.15rem;
    }
    
    span {
      font-size: 0.75rem;
      display: none;
    }
  }
`;

const BannerButton = styled.button`
  padding: 0.75rem 2rem;
  background: #1a1a2e;
  color: #D4AF37;
  border: none;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
  }
`;

export default TreasureHuntPage;
